/**
 * DOM interactions and browser-specific code
 *
 * Unified architecture:
 * - Single dataset (templates + components combined)
 * - Rows rendered once at page load with all context spans
 * - CSS toggles span visibility for instant tab/category switching
 * - JS filters rows for type/tab/category/search
 * - Mutex locks prevent concurrent operations
 */

import {
  getColumnsForType,
  getDefaultDirection as getSchemaDefaultDirection,
  getDefaultSortForType as getSchemaDefaultSortForType,
  getSortableColumnsForType,
} from "./columns.js";
import { buildCacheKey, clearSortCache, getSortedIndices, log, matchesSearch, normalizeSearchValue } from "./core.js";
import { renderAllRows } from "./render.js";
import { initSparkline } from "./sparklines.js";
import { buildNoResultsRow as buildNoResultsRowForType, mountTableStructure } from "./table-structure.js";

// ============================================
// STATE
// ============================================

// Unified state
const state = {
  // View state
  type: "template", // "template" | "component" | "creator"
  tab: "all", // "all", "free", "paid"
  period: "lastWeek", // "allTime" or "lastWeek"
  category: null, // category ID or null
  search: "", // search query

  // Search field filters
  searchFields: {
    searchRank: true,
    searchChange: true,
    searchPrice: true,
    searchName: true,
    searchAuthor: true,
  },

  // Sort state
  sortColumn: "rank",
  sortDirection: "asc", // "asc" or "desc"

  // Data
  allItems: [], // Raw items from JSON
  allRows: [], // Pre-rendered rows: [{html, item, index}, ...]
  filteredRows: [], // Currently filtered rows (subset of allRows)
  sortedIndices: [], // Sorted indices into filteredRows

  // Clusterize instance
  clusterize: null,

  // Category metadata
  templateCategoryMeta: {},
  templateCategoryTree: [],
  componentCategoryMeta: {},
  componentCategoryTree: [],
  // Legacy aliases (template categories)
  categoryMeta: {},
  categoryTree: [],
  // API-backed runtime mode (ranks-with-api.html)
  api: {
    enabled: false,
    cache: new Map(),
    activeContextKey: null,
    lastUpdated: null,
    inFlightByContextKey: new Map(),
  },
};

// Mutex locks for operations
const locks = {
  sorting: false,
  filtering: false,
  rendering: false,
};

const perfState = {
  enabled: false,
  history: [],
  maxHistory: 200,
};

const filterCache = new Map();
const FILTER_CACHE_MAX_SIZE = 200;
const MANAGED_URL_KEYS = new Set(["type", "price", "period", "category", "sorting", "direction", "search"]);
const PATH_STATE_QUERY_OVERRIDE_KEYS = new Set(["type", "price", "period", "category"]);
const MANAGED_QUERY_ONLY_KEYS = new Set(["sorting", "direction", "search"]);
const THEME_STORAGE_KEY = "ranks_theme";
const THEME_CHOICES = new Set(["dark", "light"]);
const THEME_CYCLE_ORDER = ["dark", "light"];
const DEFAULT_THEME_CHOICE = "dark";
const FALLBACK_EFFECTIVE_THEME = "dark";
const URL_TYPE_ALIASES = {
  template: "templates",
  templates: "templates",
  component: "components",
  components: "components",
  creator: "creators",
  creators: "creators",
};
const URL_PATH_TYPE_ALIASES = {
  template: "templates",
  templates: "templates",
  component: "components",
  components: "components",
  creator: "creators",
  creators: "creators",
};
const URL_TYPE_TO_STATE = {
  templates: "template",
  components: "component",
  creators: "creator",
};
const STATE_TO_URL_TYPE = {
  template: "templates",
  component: "components",
  creator: "creators",
};
const DEFAULT_STATE = {
  type: "template",
  tab: "all",
  period: "lastWeek",
  sortColumn: "rank",
  sortDirection: "asc",
  category: null,
  search: "",
};
const API_MODE_FLAG = "api-v2";
const API_SESSION_CACHE_PREFIX = "ranks_api_v2_context__";
let isApplyingUrlState = false;
let onThemeStorageChange = null;

export function normalizeThemePreference(rawValue) {
  const normalized = String(rawValue || "")
    .trim()
    .toLowerCase();
  return THEME_CHOICES.has(normalized) ? normalized : DEFAULT_THEME_CHOICE;
}

export function resolveEffectiveTheme(preference, mediaQueryList = null, fallback = FALLBACK_EFFECTIVE_THEME) {
  const normalizedPreference = normalizeThemePreference(preference);
  if (normalizedPreference === "dark" || normalizedPreference === "light") return normalizedPreference;
  return fallback;
}

export function readThemePreference() {
  if (typeof window === "undefined") return DEFAULT_THEME_CHOICE;
  try {
    const stored = window.localStorage?.getItem(THEME_STORAGE_KEY);
    return normalizeThemePreference(stored);
  } catch (_error) {
    return DEFAULT_THEME_CHOICE;
  }
}

export function writeThemePreference(preference) {
  if (typeof window === "undefined") return;
  const normalizedPreference = normalizeThemePreference(preference);
  try {
    window.localStorage?.setItem(THEME_STORAGE_KEY, normalizedPreference);
  } catch (_error) {
    // no-op: blocked storage should not break runtime theme updates
  }
}

export function applyTheme(effectiveTheme, preference = DEFAULT_THEME_CHOICE) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const normalizedEffective = effectiveTheme === "light" ? "light" : "dark";
  root.setAttribute("data-theme", normalizedEffective);
  root.setAttribute("data-theme-preference", normalizeThemePreference(preference));
}

function getSortColumnsForType(type) {
  return getSortableColumnsForType(type);
}

function getDefaultSortForType(type) {
  return getSchemaDefaultSortForType(type);
}

function getDefaultDirection(type, column) {
  return getSchemaDefaultDirection(type, String(column || ""));
}

function normalizeCanonicalType(rawType) {
  const normalized = String(rawType || "")
    .trim()
    .toLowerCase();
  return URL_TYPE_ALIASES[normalized] || null;
}

function normalizePriceParam(rawPrice) {
  const normalized = String(rawPrice || "")
    .trim()
    .toLowerCase();
  if (normalized === "mixedprice") return "all";
  if (normalized === "free" || normalized === "paid" || normalized === "all") return normalized;
  return null;
}

function normalizePathPriceParam(rawPrice) {
  const normalized = String(rawPrice || "")
    .trim()
    .toLowerCase();
  if (normalized === "mixedprice") return "all";
  if (normalized === "free" || normalized === "paid") return normalized;
  return null;
}

function normalizePeriodParam(rawPeriod) {
  const normalized = String(rawPeriod || "")
    .trim()
    .toLowerCase();
  if (normalized === "lastweek") return "lastWeek";
  if (normalized === "alltime") return "allTime";
  return null;
}

function normalizeDirectionParam(rawDirection) {
  const normalized = String(rawDirection || "")
    .trim()
    .toLowerCase();
  if (normalized === "asc" || normalized === "desc") return normalized;
  return null;
}

function getCategoryMetaForType(type, options = {}) {
  if (type === "component") return options.componentCategoryMeta || {};
  return options.templateCategoryMeta || options.categoryMeta || {};
}

export function resolveUrlStateFromSearchParams(searchParams, options = {}) {
  const normalizedParams =
    searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams || "");
  const canonicalType = normalizeCanonicalType(normalizedParams.get("type")) || STATE_TO_URL_TYPE[DEFAULT_STATE.type];
  const type = URL_TYPE_TO_STATE[canonicalType] || DEFAULT_STATE.type;
  const defaultsForType = getDefaultSortForType(type);
  const sortColumns = getSortColumnsForType(type);

  const requestedPrice = normalizePriceParam(normalizedParams.get("price"));
  const requestedPeriod = normalizePeriodParam(normalizedParams.get("period"));
  const requestedSorting = String(normalizedParams.get("sorting") || "")
    .trim()
    .toLowerCase();
  const requestedDirection = normalizeDirectionParam(normalizedParams.get("direction"));
  const requestedCategory = String(normalizedParams.get("category") || "")
    .trim()
    .toLowerCase();
  const requestedSearch = String(normalizedParams.get("search") || "").trim();

  const nextState = {
    type,
    tab: type === "creator" ? "all" : requestedPrice || DEFAULT_STATE.tab,
    period: type === "creator" ? "lastWeek" : requestedPeriod || DEFAULT_STATE.period,
    category: null,
    search: requestedSearch,
    sortColumn: defaultsForType.sortColumn,
    sortDirection: defaultsForType.sortDirection,
  };

  if (requestedSorting && sortColumns.has(requestedSorting)) {
    nextState.sortColumn = requestedSorting;
  }
  nextState.sortDirection = requestedDirection || getDefaultDirection(type, nextState.sortColumn);

  const categoryMetaForType = getCategoryMetaForType(type, options);
  if (requestedCategory && Object.hasOwn(categoryMetaForType, requestedCategory)) {
    nextState.category = requestedCategory;
  }

  if (type === "creator") {
    nextState.category = null;
    nextState.tab = "all";
    nextState.period = "lastWeek";
  }

  return nextState;
}

function normalizePath(pathname) {
  const raw = typeof pathname === "string" ? pathname : "/";
  const normalized = raw.replace(/\/+/g, "/");
  if (normalized.length > 1 && normalized.endsWith("/")) {
    return normalized.slice(0, -1);
  }
  return normalized || "/";
}

function parseStatePath(pathname) {
  const normalizedPath = normalizePath(pathname);
  const segments = normalizedPath
    .split("/")
    .map((segment) => segment.trim().toLowerCase())
    .filter(Boolean);
  if (segments.length === 0) return null;

  const canonicalType = URL_PATH_TYPE_ALIASES[segments[0]] || null;
  if (!canonicalType) return null;

  if (canonicalType === "creators") {
    return { type: "creators", malformed: segments.length > 1 };
  }

  const [, ...rest] = segments;
  if (rest.length === 0) {
    return { type: canonicalType };
  }

  if (rest.length === 1) {
    const [partA] = rest;
    if (normalizePeriodParam(partA)) return { type: canonicalType, period: partA };
    if (normalizePathPriceParam(partA)) return { type: canonicalType, price: partA };
    return { type: canonicalType, category: partA };
  }

  if (rest.length === 2) {
    const [partA, partB] = rest;
    const periodA = normalizePeriodParam(partA);
    const priceB = normalizePathPriceParam(partB);
    if (periodA && priceB) return { type: canonicalType, period: partA, price: partB };

    const periodB = normalizePeriodParam(partB);
    if (periodB) return { type: canonicalType, category: partA, period: partB };

    if (priceB) return { type: canonicalType, category: partA, price: partB };

    return { type: canonicalType, malformed: true };
  }

  if (rest.length === 3) {
    const [category, period, price] = rest;
    if (!normalizePeriodParam(period) || !normalizePathPriceParam(price)) {
      return { type: canonicalType, malformed: true };
    }
    return { type: canonicalType, category, period, price };
  }

  return { type: canonicalType, malformed: true };
}

export function resolveUrlStateFromLocation(input = {}, options = {}) {
  const pathname = typeof input.pathname === "string" ? input.pathname : "/";
  const searchParams =
    input.searchParams instanceof URLSearchParams
      ? input.searchParams
      : new URLSearchParams(typeof input.search === "string" ? input.search : "");

  const pathState = parseStatePath(pathname);
  if (!pathState) {
    return resolveUrlStateFromSearchParams(searchParams, options);
  }

  const mergedParams = new URLSearchParams(searchParams);
  for (const key of PATH_STATE_QUERY_OVERRIDE_KEYS) {
    mergedParams.delete(key);
  }
  mergedParams.set("type", pathState.type);
  if (pathState.type === "creators") {
    mergedParams.delete("price");
    mergedParams.delete("period");
    mergedParams.delete("category");
  } else {
    mergedParams.set("period", pathState.period || "");
    mergedParams.set("price", pathState.price || "");
    if (pathState.category) {
      mergedParams.set("category", pathState.category);
    } else {
      mergedParams.delete("category");
    }
  }

  return resolveUrlStateFromSearchParams(mergedParams, options);
}

function shouldEmitParam(key, value, currentState) {
  if (value === null || value === undefined || value === "") return false;
  if (!MANAGED_QUERY_ONLY_KEYS.has(key)) return false;
  if (key === "sorting") {
    return value !== getDefaultSortForType(currentState.type).sortColumn;
  }
  if (key === "direction") {
    return value !== getDefaultDirection(currentState.type, currentState.sortColumn);
  }
  return true;
}

export function serializeStateToCanonicalPath(currentState) {
  const typeValue = STATE_TO_URL_TYPE[currentState.type] || "templates";
  if (typeValue === "creators") return "/creators";
  const isDefaultTemplateView =
    typeValue === "templates" &&
    currentState.period === "lastWeek" &&
    String(currentState.tab || "all").toLowerCase() === "all" &&
    !currentState.category;
  if (isDefaultTemplateView) return "/";
  const periodValue = currentState.period === "allTime" ? "alltime" : "lastweek";
  const normalizedTab = String(currentState.tab || "all").toLowerCase();
  const priceValue = normalizedTab === "all" ? "mixedprice" : normalizedTab;
  const categoryValue = currentState.category ? String(currentState.category).toLowerCase() : "";
  if (categoryValue) {
    return `/${typeValue}/${categoryValue}/${periodValue}/${priceValue}`;
  }
  return `/${typeValue}/${periodValue}/${priceValue}`;
}

export function serializeStateToCanonicalQuery(currentState, baseSearchParams = null) {
  const base =
    baseSearchParams instanceof URLSearchParams ? baseSearchParams : new URLSearchParams(baseSearchParams || "");
  const next = new URLSearchParams(base);
  for (const key of MANAGED_URL_KEYS) next.delete(key);

  const sortingValue = String(
    currentState.sortColumn || getDefaultSortForType(currentState.type).sortColumn,
  ).toLowerCase();
  const directionValue = String(
    currentState.sortDirection || getDefaultDirection(currentState.type, sortingValue),
  ).toLowerCase();
  const searchValue = String(currentState.search || "").trim();

  const managed = {
    sorting: sortingValue,
    direction: directionValue,
    search: searchValue,
  };

  for (const [key, value] of Object.entries(managed)) {
    if (shouldEmitParam(key, value, currentState)) {
      next.set(key, value);
    }
  }
  return next;
}

export function serializeStateToCanonicalParams(currentState, baseSearchParams = null) {
  return serializeStateToCanonicalQuery(currentState, baseSearchParams);
}

function syncUrlWithState() {
  if (typeof window === "undefined" || isApplyingUrlState) return;
  const currentPath = normalizePath(window.location.pathname);
  const current = new URLSearchParams(window.location.search);
  const canonicalPath = serializeStateToCanonicalPath(state);
  const canonicalQuery = serializeStateToCanonicalQuery(state, current);
  const nextQuery = canonicalQuery.toString();
  const currentQuery = current.toString();
  if (canonicalPath === currentPath && nextQuery === currentQuery) return;
  const nextUrl = `${canonicalPath}${nextQuery ? `?${nextQuery}` : ""}${window.location.hash || ""}`;
  window.history.replaceState({}, "", nextUrl);
}

function updateThemeSwitchUi(preference) {
  const normalizedPreference = normalizeThemePreference(preference);
  const switchRoot = document.getElementById("theme-switch");
  if (!switchRoot) return;
  switchRoot.setAttribute("data-theme-choice", normalizedPreference);
  const buttons = switchRoot.querySelectorAll(".theme-switch-btn");
  buttons.forEach((button) => {
    const choice = button.getAttribute("data-theme-choice");
    const isActive = choice === normalizedPreference;
    button.classList.toggle("active", isActive);
    button.setAttribute("aria-pressed", isActive ? "true" : "false");
  });
  const cycleButton = document.getElementById("theme-btn-cycle");
  if (cycleButton) {
    cycleButton.setAttribute("data-theme-choice", normalizedPreference);
    const nextPreference = getNextThemePreference(normalizedPreference);
    const label = `Theme: ${normalizedPreference}. Tap to switch to ${nextPreference}.`;
    cycleButton.setAttribute("aria-label", label);
    cycleButton.setAttribute("title", label);
  }
}

function getNextThemePreference(preference) {
  const normalized = normalizeThemePreference(preference);
  const index = THEME_CYCLE_ORDER.indexOf(normalized);
  const nextIndex = index < 0 ? 0 : (index + 1) % THEME_CYCLE_ORDER.length;
  return THEME_CYCLE_ORDER[nextIndex];
}

function setThemePreference(preference) {
  const normalizedPreference = normalizeThemePreference(preference);
  writeThemePreference(normalizedPreference);
  const effectiveTheme = resolveEffectiveTheme(normalizedPreference);
  applyTheme(effectiveTheme, normalizedPreference);
  updateThemeSwitchUi(normalizedPreference);
}

function initThemeController() {
  const preference = readThemePreference();
  const effectiveTheme = resolveEffectiveTheme(preference);
  applyTheme(effectiveTheme, preference);
  updateThemeSwitchUi(preference);

  const switchRoot = document.getElementById("theme-switch");
  if (!switchRoot || switchRoot.dataset.bound === "1") return;
  switchRoot.dataset.bound = "1";

  switchRoot.addEventListener("click", (event) => {
    const clicked = event.target instanceof Element ? event.target : null;
    if (!clicked) return;
    const cycleTarget = clicked.closest(".theme-cycle-btn");
    if (cycleTarget) {
      const currentPreference = normalizeThemePreference(readThemePreference());
      const nextPreference = getNextThemePreference(currentPreference);
      setThemePreference(nextPreference);
      return;
    }
    const target = clicked.closest(".theme-switch-btn");
    if (!target) return;
    setThemePreference(target.getAttribute("data-theme-choice"));
  });

  if (!onThemeStorageChange && typeof window !== "undefined") {
    onThemeStorageChange = (event) => {
      if (!event || event.key !== THEME_STORAGE_KEY) return;
      const preferenceFromStorage = normalizeThemePreference(event.newValue);
      const effectiveThemeFromStorage = resolveEffectiveTheme(preferenceFromStorage);
      applyTheme(effectiveThemeFromStorage, preferenceFromStorage);
      updateThemeSwitchUi(preferenceFromStorage);
    };
    window.addEventListener("storage", onThemeStorageChange);
  }
}

function setBootingState(isBooting) {
  if (typeof document === "undefined" || !document.body) return;
  document.body.classList.toggle("ranks-booting", Boolean(isBooting));
}

function applyCurrentStateToUi({ closeDropdown = false, scrollTop = false } = {}) {
  updateViewClass();
  renderCategoryTree();
  updateTabButtonStyles();
  updateControlDisabling();
  updateSearchFieldControls();
  updateColumnHeadersForMode();
  updatePeriodButtonStyles();
  updateCategoryDropdownVisibility();
  const activeMeta = getActiveCategoryMeta();
  const categoryName =
    state.category && activeMeta[state.category] ? activeMeta[state.category].name : getDefaultCategoryDropdownLabel();
  updateCategoryDropdownText(categoryName);
  updateCategorySelectedState(state.category);
  const searchInput = document.getElementById("rankings-search");
  if (searchInput) {
    searchInput.value = state.search || "";
    searchInput.classList.toggle("search-active", Boolean(state.search));
  }
  refresh();
  if (closeDropdown) closeCategoryDropdown();
  if (scrollTop) {
    const scroll = document.getElementById("clusterize-scroll");
    if (scroll) scroll.scrollTop = 0;
  }
}

function detectPerfEnabled() {
  if (typeof window === "undefined") return false;
  if (window.__RANKS_PERF__ === true) return true;
  const params = new URLSearchParams(window.location.search);
  return params.get("perf") === "1";
}

function recordPerf(entry) {
  if (!perfState.enabled) return;
  perfState.history.push(entry);
  if (perfState.history.length > perfState.maxHistory) {
    perfState.history.shift();
  }
  console.log("[RANKS_PERF]", entry);
}

function buildNoResultsRow(message = "No results found") {
  return buildNoResultsRowForType(state.type, message);
}

function buildFooterStretchRow() {
  const normalizedType = state.type === "creator" ? "creator" : "template";
  const columns = getColumnsForType(normalizedType);
  const cells = columns.map((column) => `<td class="${column.cellClass || ""}"></td>`).join("");
  return `<tr class="tfoot-spacer-row" aria-hidden="true">${cells}</tr>`;
}

function clearFilterCache() {
  filterCache.clear();
}

function buildFilterCacheKey() {
  return buildCacheKey({
    type: state.type,
    category: state.category,
    tab: state.tab,
    period: state.period,
    column: "_",
    direction: "_",
    search: state.search,
  }).concat(
    `-sr${state.searchFields.searchRank ? 1 : 0}`,
    `-sc${state.searchFields.searchChange ? 1 : 0}`,
    `-sp${state.searchFields.searchPrice ? 1 : 0}`,
    `-sn${state.searchFields.searchName ? 1 : 0}`,
    `-sa${state.searchFields.searchAuthor ? 1 : 0}`,
    `-n${state.allRows.length}`,
  );
}

function getActiveCategoryMeta() {
  if (state.type === "component") return state.componentCategoryMeta;
  return Object.keys(state.templateCategoryMeta || {}).length > 0 ? state.templateCategoryMeta : state.categoryMeta;
}

function getActiveCategoryTree() {
  if (state.type === "component") return state.componentCategoryTree;
  return Array.isArray(state.templateCategoryTree) && state.templateCategoryTree.length > 0
    ? state.templateCategoryTree
    : state.categoryTree;
}

function getAllCategoryIds() {
  return Array.from(
    new Set([
      ...Object.keys(state.templateCategoryMeta || {}),
      ...Object.keys(state.componentCategoryMeta || {}),
      ...Object.keys(state.categoryMeta || {}),
    ]),
  );
}

const DEFAULT_CATEGORY_DROPDOWN_LABELS = new Set([
  "Category",
  "Categories",
  "Template Categories",
  "Component Categories",
]);

function getDefaultCategoryDropdownLabel() {
  return "Category";
}

function getAllCategoriesOptionLabel() {
  if (state.type === "template") return "Template Categories";
  if (state.type === "component") return "Component Categories";
  return "Categories";
}

function normalizeCategoryDisplayName(name) {
  if (typeof name !== "string") return name;
  return name
    .replace(/\s*Templates?\s*$/i, "")
    .replace(/\s*Components?\s*$/i, "")
    .replace(/\bCms\b/g, "CMS")
    .trim();
}

// ============================================
// MUTEX HELPERS
// ============================================

function acquireLock(lockName) {
  if (locks[lockName]) {
    log(`[MUTEX] ${lockName} blocked - operation in progress`);
    return false;
  }
  locks[lockName] = true;
  log(`[MUTEX] ${lockName} acquired`);
  return true;
}

function releaseLock(lockName) {
  locks[lockName] = false;
  log(`[MUTEX] ${lockName} released`);
}

// ============================================
// VIEW CONTEXT HELPERS
// ============================================

/**
 * Get the current view context for CSS class and sorting
 * Returns: "alltime-all", "alltime-free", "alltime-paid",
 *          "lastWeek-all", "lastWeek-free", "lastWeek-paid", or a category ID
 */
function getCurrentContext() {
  if (state.type === "creator") return "creators";
  if (state.category) {
    const categoryPeriod = state.period === "lastWeek" ? "lastweek" : "alltime";
    return `${state.category}-${categoryPeriod}-${state.tab}`;
  }
  if (state.period === "lastWeek") {
    return `lastWeek-${state.tab}`; // "lastWeek-all", "lastWeek-free", "lastWeek-paid"
  }
  return `alltime-${state.tab}`; // "alltime-all", "alltime-free", "alltime-paid"
}

function isApiModeEnabled() {
  if (typeof window === "undefined") return false;
  const source = String(window.FR_RANKS_DATA_SOURCE || "")
    .trim()
    .toLowerCase();
  return source === API_MODE_FLAG || window.FR_RANKS_USE_API_JSON === true;
}

function getApiType() {
  if (state.type === "component") return "components";
  if (state.type === "creator") return "creators";
  return "templates";
}

function toApiPeriod(period) {
  return period === "lastWeek" ? "lastweek" : "alltime";
}

function buildApiContextKey() {
  const type = getApiType();
  const period = toApiPeriod(state.period);
  const price = state.tab;
  const category = state.category ? String(state.category) : "_";
  return `${type}|${period}|${price}|${category}`;
}

function getApiSessionStorageKey(contextKey) {
  return `${API_SESSION_CACHE_PREFIX}${contextKey}`;
}

function buildApiUrlForCurrentState() {
  const params = new URLSearchParams();
  params.set("v", "2");
  params.set("type", getApiType());
  params.set("period", toApiPeriod(state.period));
  params.set("price", state.tab);
  params.set("limit", "100000");
  if (state.category) {
    params.set("category", String(state.category));
  }
  const endpointRaw =
    typeof window !== "undefined" && typeof window.FR_RANKS_API_ENDPOINT === "string"
      ? window.FR_RANKS_API_ENDPOINT
      : "/api/json";
  const endpoint = endpointRaw.trim() || "/api/json";
  const separator = endpoint.includes("?") ? "&" : "?";
  return `${endpoint}${separator}${params.toString()}`;
}

function mapApiTypeToInternalType(value) {
  if (value === "components") return "component";
  if (value === "creators") return "creator";
  return "template";
}

function toInternalChangeValue(row) {
  if (row?.changeLabel === "new") return "new";
  if (row?.changeLabel === "comeback") return "comeback";
  if (row?.changeLabel === "price_change") return "price_change";
  if (typeof row?.delta1d === "number") return row.delta1d;
  return null;
}

function applyApiPayload(payload) {
  const context =
    typeof payload?.contextKey === "string" && payload.contextKey ? payload.contextKey : getCurrentContext();
  const entities = typeof payload?.entities === "object" && payload.entities ? payload.entities : {};
  const contextRows = Array.isArray(payload?.contextRows) ? payload.contextRows : [];

  const items = contextRows.map((row, index) => {
    const entity = entities[row.id] || {};
    const apiType = String(entity.type || payload?.filters?.type || "templates");
    const internalType = mapApiTypeToInternalType(apiType);

    if (internalType === "creator") {
      const creatorStats = row.creatorStats || {};
      const creatorSlug = String(entity.creatorSlug || "").trim();
      const creatorUrl =
        typeof entity.url === "string" && entity.url
          ? entity.url
          : creatorSlug
            ? `https://framer.com/@${creatorSlug}`
            : "https://framer.com/marketplace/";
      return {
        id: row.id || `creator-${index}`,
        type: "creator",
        name: entity.name || "Unknown creator",
        creatorSlug,
        creatorUrl,
        url: creatorUrl,
        thumbnail: entity.thumbnailUrl || null,
        price: "-",
        authorName: creatorSlug ? `@${creatorSlug}` : "-",
        authorUrl: creatorUrl,
        ranks: {},
        categories: [],
        badges: { crowns: [], medals: [], medalScore: 0 },
        creatorStats: {
          totalItems: Number(creatorStats.totalItems || 0),
          totalTemplates: Number(creatorStats.totalTemplates || 0),
          totalComponents: Number(creatorStats.totalComponents || 0),
        },
      };
    }

    const priceLabel = String(row.priceLabel || entity.basePrice || "-");
    const thumbnailUrl = typeof entity.thumbnailUrl === "string" && entity.thumbnailUrl ? entity.thumbnailUrl : null;
    const categories = Array.isArray(row.categories)
      ? row.categories
      : Array.isArray(entity.category)
        ? entity.category
        : typeof entity.category === "string" && entity.category
          ? [entity.category]
          : [];
    const medals = Array.isArray(row?.badges?.medals) ? row.badges.medals : [];
    const crowns = Array.isArray(row?.badges?.crowns) ? row.badges.crowns : [];
    return {
      id: row.id || `${internalType}-${index}`,
      type: internalType,
      name: entity.name || "",
      url: entity.url || "",
      price: priceLabel,
      previousPrice: row.previousPrice ?? null,
      thumbnail: thumbnailUrl,
      allImages: thumbnailUrl ? { "64w": thumbnailUrl } : null,
      authorName: entity.authorName || "",
      authorUrl: entity.authorUrl || null,
      categories,
      badges: {
        crowns,
        medals,
        medalScore: Number(row?.badges?.medalScore || 0),
      },
      ranks: {
        [context]: [Number(row.rank || 0), toInternalChangeValue(row)],
      },
      trend: row.trend ? { [context]: row.trend } : {},
    };
  });

  state.allItems = items;
  state.templateCategoryMeta = payload.templateCategoryMeta || {};
  state.templateCategoryTree = payload.templateCategoryTree || [];
  state.componentCategoryMeta = payload.componentCategoryMeta || {};
  state.componentCategoryTree = payload.componentCategoryTree || [];
  state.categoryMeta = state.templateCategoryMeta;
  state.categoryTree = state.templateCategoryTree;

  if (typeof window !== "undefined") {
    window.__RANKS_TEMPLATE_CATEGORY_META__ = state.templateCategoryMeta;
    window.__RANKS_COMPONENT_CATEGORY_META__ = state.componentCategoryMeta;
    window.__RANKS_CATEGORY_META__ = {
      ...state.templateCategoryMeta,
      ...state.componentCategoryMeta,
    };
    const uiFlags = payload.uiFlags || {};
    window.__RANKS_SPARKLINE_LABEL_MODE__ = uiFlags.sparklineLabelMode === "all" ? "all" : "none";
    window.__RANKS_SPARKLINE_TITLE_ATTR__ = uiFlags.sparklineTitleAttr === true;
  }

  const dateEl = document.getElementById("current-date");
  if (dateEl) {
    const lastUpdated = typeof payload.lastUpdated === "string" ? payload.lastUpdated : "";
    dateEl.textContent = lastUpdated ? lastUpdated.replace("T", " ").replace("Z", " UTC") : "-";
  }
}

async function loadApiContextForCurrentState(force = false) {
  const contextKey = buildApiContextKey();

  if (!force && state.api.activeContextKey === contextKey && state.allItems.length > 0) {
    return;
  }

  if (state.api.inFlightByContextKey.has(contextKey)) {
    await state.api.inFlightByContextKey.get(contextKey);
    return;
  }

  const loadPromise = (async () => {
    if (!force) {
      const memoryCached = state.api.cache.get(contextKey);
      if (memoryCached) {
        if (!state.api.lastUpdated || state.api.lastUpdated === memoryCached.lastUpdated) {
          applyApiPayload(memoryCached.payload);
          state.api.activeContextKey = contextKey;
          return;
        }
      }
      try {
        const rawSession = sessionStorage.getItem(getApiSessionStorageKey(contextKey));
        if (rawSession) {
          const cached = JSON.parse(rawSession);
          if (!state.api.lastUpdated || state.api.lastUpdated === cached?.lastUpdated) {
            state.api.cache.set(contextKey, cached);
            applyApiPayload(cached.payload);
            state.api.activeContextKey = contextKey;
            return;
          }
        }
      } catch (_error) {
        // ignore cache parse/storage errors
      }
    }

    const response = await fetch(buildApiUrlForCurrentState(), {
      method: "GET",
      cache: "no-store",
      headers: {
        "X-Client-Source": "ranks-with-api",
        "X-Client-Version": "v2-experiment",
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to load API context: ${response.status}`);
    }
    const payload = await response.json();
    if (!payload?.ok) {
      throw new Error("API payload returned not ok");
    }

    if (state.api.lastUpdated && payload.lastUpdated && state.api.lastUpdated !== payload.lastUpdated) {
      state.api.cache.clear();
      try {
        const keysToDelete = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key?.startsWith(API_SESSION_CACHE_PREFIX)) keysToDelete.push(key);
        }
        keysToDelete.forEach((key) => {
          sessionStorage.removeItem(key);
        });
      } catch (_error) {
        // ignore
      }
    }

    const cached = {
      lastUpdated: payload.lastUpdated || null,
      payload,
    };
    state.api.lastUpdated = payload.lastUpdated || state.api.lastUpdated;
    state.api.cache.set(contextKey, cached);
    try {
      sessionStorage.setItem(getApiSessionStorageKey(contextKey), JSON.stringify(cached));
    } catch (_error) {
      // ignore storage failures
    }
    applyApiPayload(payload);
    renderRows();
    clearFilterCache();
    state.api.activeContextKey = contextKey;
  })();

  state.api.inFlightByContextKey.set(contextKey, loadPromise);
  try {
    await loadPromise;
  } finally {
    state.api.inFlightByContextKey.delete(contextKey);
  }
}

/**
 * Inject CSS rules for category views dynamically
 */
function injectCategoryCssRules() {
  // Check if already injected
  if (document.getElementById("category-view-styles")) return;

  const style = document.createElement("style");
  style.id = "category-view-styles";

  // Generate rules for all categories
  const rules = [];
  for (const categoryId of getAllCategoryIds()) {
    for (const period of ["alltime", "lastweek"]) {
      for (const tab of ["all", "free", "paid"]) {
        const context = `${categoryId}-${period}-${tab}`;
        rules.push(`.view-${context} .rank-cell .r-${context} { display: inline !important; }`);
        rules.push(`.view-${context} .change-cell .c-${context} { display: inline !important; }`);
        rules.push(`.view-${context} .trend-cell .t-${context} { display: inline !important; }`);
      }
    }
  }

  // Add alltime and lastWeek context rules
  for (const tab of ["all", "free", "paid"]) {
    rules.push(`.view-alltime-${tab} .rank-cell .r-alltime-${tab} { display: inline !important; }`);
    rules.push(`.view-alltime-${tab} .change-cell .c-alltime-${tab} { display: inline !important; }`);
    rules.push(`.view-alltime-${tab} .trend-cell .t-alltime-${tab} { display: inline !important; }`);
    rules.push(`.view-lastWeek-${tab} .rank-cell .r-lastWeek-${tab} { display: inline !important; }`);
    rules.push(`.view-lastWeek-${tab} .change-cell .c-lastWeek-${tab} { display: inline !important; }`);
    rules.push(`.view-lastWeek-${tab} .trend-cell .t-lastWeek-${tab} { display: inline !important; }`);
  }

  style.textContent = rules.join("\n");
  document.head.appendChild(style);
  log(`[CSS] Injected ${rules.length / 2} category view rules`);
}

/**
 * Update container CSS class for span visibility
 */
function updateViewClass() {
  const container = document.getElementById("rankings-table-container");
  if (!container) return;

  // Remove all view-* classes
  const classes = Array.from(container.classList);
  for (const cls of classes) {
    if (cls.startsWith("view-")) {
      container.classList.remove(cls);
    }
  }

  // Add current view class
  const context = getCurrentContext();
  container.classList.add(`view-${context}`);
  container.classList.toggle("components-table", state.type === "component");
  container.classList.toggle("creators-table", state.type === "creator");
  container.classList.toggle("category-view", Boolean(state.category));
  log(`[CSS] View class set to: view-${context}`);
}

// ============================================
// FILTERING
// ============================================

/**
 * Filter rows based on current state
 * Returns indices into allRows that match current filters
 */
function filterRows() {
  log(
    `[FILTER] Filtering: type=${state.type}, tab=${state.tab}, category=${state.category || "none"}, search="${state.search}"`,
  );
  const startTime = performance.now();

  const filterCacheKey = buildFilterCacheKey();
  if (filterCache.has(filterCacheKey)) {
    const cachedRows = filterCache.get(filterCacheKey);
    if (cachedRows) {
      log(`[FILTER] Cache HIT: ${filterCacheKey} (${cachedRows.length} rows)`);
      return cachedRows;
    }
  }

  const filtered = [];
  const query = normalizeSearchValue(state.search);

  for (let i = 0; i < state.allRows.length; i++) {
    const row = state.allRows[i];
    const item = row.item;

    // Filter by type
    if (item.type !== state.type) continue;

    if (state.type === "creator") {
      if (query && !matchesSearch(item, query, state.searchFields)) continue;
      filtered.push(row);
      continue;
    }

    // Filter by tab (free/paid items) only for global views.
    // In category mode we trust category context ranks (e.g. cat-lastweek-free),
    // not global item.price, which can be stale relative to category snapshots.
    if (!state.category) {
      if (state.tab === "free" && item.price !== "Free") continue;
      if (state.tab === "paid" && item.price === "Free") continue;
    }

    // Filter by category (templates only)
    if (state.category && !item.categories?.includes(state.category)) continue;

    // Filter by period/context availability
    if (state.category || state.period === "lastWeek") {
      const context = getCurrentContext();
      const entry = item.ranks?.[context];
      if (!entry || entry[0] === null) continue;
    }

    // Filter by search
    if (query && !matchesSearch(item, query, state.searchFields)) continue;

    filtered.push(row);
  }

  const duration = Math.round(performance.now() - startTime);
  log(`[FILTER] Filtered to ${filtered.length} rows in ${duration}ms`);

  while (filterCache.size >= FILTER_CACHE_MAX_SIZE) {
    const oldestKey = filterCache.keys().next().value;
    filterCache.delete(oldestKey);
  }
  filterCache.set(filterCacheKey, filtered);

  return filtered;
}

// ============================================
// SORTING
// ============================================

/**
 * Sort the filtered rows by current sort settings
 * Note: This function does NOT acquire locks - caller must handle locking
 */
function sortFilteredRows() {
  const startTime = performance.now();
  const context = getCurrentContext();

  // Build explicit cache key from full filter state
  const cacheKey = buildCacheKey({
    type: state.type,
    category: state.category,
    tab: state.tab,
    period: state.period,
    column: state.sortColumn,
    direction: state.sortDirection,
    search: state.search,
  });

  log(
    `[SORT] Sorting: column=${state.sortColumn}, direction=${state.sortDirection}, context=${context}, cacheKey=${cacheKey}`,
  );

  // Get items from filtered rows for sorting
  const items = state.filteredRows.map((r) => r.item);

  // Get sorted indices with explicit cache key
  const indices = getSortedIndices(items, state.sortColumn, state.sortDirection, context, cacheKey);

  // Reorder filtered rows
  state.sortedIndices = indices;

  const duration = Math.round(performance.now() - startTime);
  log(`[SORT] Sorted ${indices.length} rows in ${duration}ms`);
}

// ============================================
// CLUSTERIZE UPDATE
// ============================================

/**
 * Update Clusterize with current sorted/filtered rows
 */
function updateClusterize() {
  if (!state.clusterize) return { clusterUpdateMs: 0, sparklineMs: 0, htmlRowsMs: 0, clusterMs: 0 };

  const startTime = performance.now();

  const htmlRowsStart = performance.now();
  let htmlRows = state.sortedIndices.map((i) => state.filteredRows[i].html);
  const htmlRowsMs = performance.now() - htmlRowsStart;

  if (htmlRows.length === 0) {
    const emptyMessage = state.type === "creator" ? "No creators found" : "No results found";
    htmlRows = [buildNoResultsRow(emptyMessage)];
  }
  htmlRows.push(buildFooterStretchRow());

  const updateStartedAt = performance.now();
  state.clusterize.update(htmlRows);
  const clusterUpdateMs = performance.now() - updateStartedAt;

  // Initialize sparklines for newly rendered rows
  const sparklinesStartedAt = performance.now();
  initSparklines();
  const sparklineMs = performance.now() - sparklinesStartedAt;

  const duration = Math.round(performance.now() - startTime);
  log(`[CLUSTERIZE] Updated with ${htmlRows.length} rows in ${duration}ms`);
  return { clusterUpdateMs, sparklineMs, htmlRowsMs, clusterMs: performance.now() - startTime };
}

/**
 * Initialize sparklines for all elements with data-sparkline="true"
 */
function initSparklines() {
  const sparklines = document.querySelectorAll('[data-sparkline="true"]');
  log(`[SPARKLINES] Initializing ${sparklines.length} sparklines`);

  for (const sparkline of sparklines) {
    // renderSparkline() already outputs inline SVG; skip expensive re-init.
    if (sparkline.querySelector("svg")) continue;
    initSparkline(sparkline);
  }
}

function syncFooterStretchRowHeight() {
  const scroller = document.getElementById("clusterize-scroll");
  const table = document.getElementById("rankings-table");
  const spacerRow = document.querySelector("#clusterize-content tr.tfoot-spacer-row");
  if (!scroller || !table || !spacerRow) return;

  const tableHeight = table.getBoundingClientRect().height;
  const spacerHeight = spacerRow.getBoundingClientRect().height;
  const contentHeight = Math.max(0, tableHeight - spacerHeight);
  const missingHeight = Math.max(0, Math.floor(scroller.clientHeight - contentHeight));
  spacerRow.querySelectorAll("td").forEach((cell) => {
    cell.style.height = `${missingHeight}px`;
  });
}

/**
 * Full refresh: filter, sort, and update display
 */
function refreshSync() {
  const refreshStartedAt = performance.now();

  // Note: Cache key now includes item count, so no need to clear manually
  const filterStart = performance.now();
  state.filteredRows = filterRows();
  const filterMs = performance.now() - filterStart;

  const sortStart = performance.now();
  sortFilteredRows();
  const sortMs = performance.now() - sortStart;

  const clusterStart = performance.now();
  const clusterMetrics = updateClusterize();
  const clusterMs = performance.now() - clusterStart;

  const indicatorsStart = performance.now();
  updateSortIndicators();
  const indicatorsMs = performance.now() - indicatorsStart;

  const titleStart = performance.now();
  updateTableTitle(); // Update static count in title
  const titleMs = performance.now() - titleStart;

  const footerStart = performance.now();
  updateTableFooter(); // Update dynamic stats in footer
  const footerMs = performance.now() - footerStart;
  syncFooterStretchRowHeight();

  const totalMs = performance.now() - refreshStartedAt;
  recordPerf({
    at: new Date().toISOString(),
    view: getCurrentContext(),
    type: state.type,
    rows: state.filteredRows.length,
    filterMs: Math.round(filterMs * 100) / 100,
    sortMs: Math.round(sortMs * 100) / 100,
    clusterMs: Math.round(clusterMs * 100) / 100,
    clusterUpdateMs: Math.round((clusterMetrics?.clusterUpdateMs ?? 0) * 100) / 100,
    htmlRowsMs: Math.round((clusterMetrics?.htmlRowsMs ?? 0) * 100) / 100,
    sparklineMs: Math.round((clusterMetrics?.sparklineMs ?? 0) * 100) / 100,
    indicatorsMs: Math.round(indicatorsMs * 100) / 100,
    titleMs: Math.round(titleMs * 100) / 100,
    footerMs: Math.round(footerMs * 100) / 100,
    totalMs: Math.round(totalMs * 100) / 100,
  });
}

async function refresh() {
  if (state.api.enabled) {
    try {
      await loadApiContextForCurrentState(false);
    } catch (error) {
      console.error("[API_MODE] Failed to refresh context:", error);
      return;
    }
  }
  refreshSync();
}

// ============================================
// EVENT HANDLERS
// ============================================

/**
 * Handle type switch (templates/components)
 */
function handleTypeSwitch(newType) {
  const isReselectingTemplatesFromCategory =
    state.type === "template" && newType === "template" && state.category !== null;
  const isReselectingComponentsFromCategory =
    state.type === "component" && newType === "component" && state.category !== null;
  const isLeavingCreators = state.type === "creator" && newType !== "creator";
  if (state.type === newType && !isReselectingTemplatesFromCategory && !isReselectingComponentsFromCategory) return;

  log(`[TYPE] Switching to ${newType}`);
  state.type = newType;
  state.category = null; // Clear category when switching type or reselecting templates

  if (newType === "creator") {
    state.period = "lastWeek";
    state.tab = "all";
    const defaultsForType = getDefaultSortForType("creator");
    state.sortColumn = defaultsForType.sortColumn;
    state.sortDirection = defaultsForType.sortDirection;
    // Carry-over search terms from templates/components often hide all creators.
    // Reset search when entering creators mode for predictable first view.
    state.search = "";
    const searchInput = document.getElementById("rankings-search");
    if (searchInput) {
      searchInput.value = "";
      searchInput.classList.remove("search-active");
    }
  }

  if (isLeavingCreators) {
    state.period = "lastWeek";
    state.tab = "all";
  }

  // Reselecting Templates/Components while in category view should snap to global lastWeek-all.
  if (isReselectingTemplatesFromCategory || isReselectingComponentsFromCategory) {
    state.period = "lastWeek";
    state.tab = "all";
  }

  // Persist tab and period across type switch by default.
  if (newType !== "creator") {
    const defaultsForType = getDefaultSortForType(newType);
    state.sortColumn = defaultsForType.sortColumn;
    state.sortDirection = defaultsForType.sortDirection;
  }

  updateViewClass();
  renderCategoryTree();
  updateCategoryDropdownText(getDefaultCategoryDropdownLabel()); // Reset dropdown text
  updateCategorySelectedState(null); // Clear selected category in dropdown
  disableFreePaidTabs(false); // Re-enable tabs
  updateCategoryDropdownVisibility(); // Ensure components-mode is set
  updateControlDisabling();
  updateSearchFieldControls();
  updateColumnHeadersForMode();
  updatePeriodButtonStyles(); // Update to reflect new default

  updateTabButtonStyles();
  refresh();
  syncUrlWithState();

  // Scroll to top
  const scroll = document.getElementById("clusterize-scroll");
  if (scroll) scroll.scrollTop = 0;

  trackEvent(newType === "component" ? "tab_clicked_components" : "tab_clicked_templates");
}

/**
 * Handle tab switch (all/free/paid)
 */
function handleTabSwitch(newTab) {
  if (state.type === "creator") return;
  if (state.tab === newTab) return;

  log(`[TAB] Switching to ${newTab}`);
  state.tab = newTab;
  if (!state.category) {
    state.category = null; // Keep existing global behavior explicit
  }
  disableFreePaidTabs(false); // Ensure they are active
  updatePeriodButtonStyles(); // Unblock period toggle

  // Update sort to match tab
  const defaultsForType = getDefaultSortForType(state.type);
  state.sortColumn = defaultsForType.sortColumn;
  state.sortDirection = defaultsForType.sortDirection;

  updateViewClass();
  updateTabButtonStyles();
  refresh();
  syncUrlWithState();

  // Scroll to top
  const scroll = document.getElementById("clusterize-scroll");
  if (scroll) scroll.scrollTop = 0;

  trackEvent(`tab_clicked_${newTab}`, { table: state.type });
}

/**
 * Handle period switch (allTime/lastWeek)
 */
function handlePeriodSwitch(newPeriod) {
  if (state.type === "creator") return;
  if (state.period === newPeriod) return;

  log(`[PERIOD] Switching to ${newPeriod}`);
  state.period = newPeriod;

  if (newPeriod === "lastWeek") {
    const defaultsForType = getDefaultSortForType(state.type);
    state.sortColumn = defaultsForType.sortColumn;
    state.sortDirection = defaultsForType.sortDirection;
    updatePeriodButtonStyles();
  } else {
    // allTime
    updatePeriodButtonStyles();
  }

  updateViewClass();
  updateTabButtonStyles();
  refresh();
  syncUrlWithState();

  // Scroll to top
  const scroll = document.getElementById("clusterize-scroll");
  if (scroll) scroll.scrollTop = 0;

  trackEvent(`period_clicked_${newPeriod}`, { table: state.type });
}

/**
 * Handle category selection
 */
function handleCategorySelect(categoryId, categoryName) {
  if (state.type === "creator") return;
  log(`[CATEGORY] Selected: ${categoryName} (${categoryId})`);

  state.category = categoryId;
  state.period = "lastWeek"; // Default category view is Last Week
  const defaultsForType = getDefaultSortForType(state.type);
  state.sortColumn = defaultsForType.sortColumn;
  state.sortDirection = defaultsForType.sortDirection;

  updateViewClass();
  updateTabButtonStyles();
  updateCategoryDropdownText(categoryName);
  updateCategorySelectedState(categoryId);
  disableFreePaidTabs(false);
  updateControlDisabling();
  updateSearchFieldControls();
  updateColumnHeadersForMode();
  updatePeriodButtonStyles();
  refresh();
  syncUrlWithState();

  closeCategoryDropdown();

  trackEvent("category_selected", { categoryId, categoryName });
}

/**
 * Handle category clear
 */
function handleCategoryClear() {
  if (state.type === "creator") return;
  log(`[CATEGORY] Cleared`);

  state.category = null;
  const defaultsForType = getDefaultSortForType(state.type);
  state.sortColumn = defaultsForType.sortColumn;
  state.sortDirection = defaultsForType.sortDirection;
  state.period = "lastWeek"; // Snap to Last Week

  updateViewClass();
  updateCategoryDropdownText(getDefaultCategoryDropdownLabel());
  updateCategorySelectedState(null);
  disableFreePaidTabs(false);
  updateControlDisabling();
  updateSearchFieldControls();
  updateColumnHeadersForMode();
  updatePeriodButtonStyles(); // Update to reflect Last Week
  refresh();
  syncUrlWithState();

  trackEvent("category_cleared");
}

function handleMedalJump(categoryId, rank, market = "all", itemType = null) {
  if (!categoryId) return;

  const nextType =
    itemType === "component" || itemType === "template"
      ? itemType
      : state.type === "component"
        ? "component"
        : "template";
  const categoryMetaForType = getCategoryMetaForType(nextType, state) || {};
  const categoryName = categoryMetaForType?.[categoryId]?.name || categoryId;
  const nextTab = market === "free" || market === "paid" ? market : "all";

  state.type = nextType;
  state.category = categoryId;
  state.tab = nextTab;
  state.period = "lastWeek";
  const defaultsForType = getDefaultSortForType(nextType);
  state.sortColumn = defaultsForType.sortColumn;
  state.sortDirection = defaultsForType.sortDirection;

  updateViewClass();
  updateTabButtonStyles();
  updateCategoryDropdownText(categoryName);
  updateCategorySelectedState(categoryId);
  disableFreePaidTabs(false);
  updateControlDisabling();
  updateSearchFieldControls();
  updateColumnHeadersForMode();
  updatePeriodButtonStyles();
  refresh();
  syncUrlWithState();
  closeCategoryDropdown();

  const scroll = document.getElementById("clusterize-scroll");
  if (scroll) scroll.scrollTop = 0;

  trackEvent("medal_jump_clicked", {
    categoryId,
    rank,
    market: nextTab,
    itemType: nextType,
  });
}

function handleCrownJump(crownPeriod, crownMarket, itemType) {
  const nextType = itemType === "component" ? "component" : "template";
  const nextPeriod = crownPeriod === "lastWeek" ? "lastWeek" : "allTime";
  const nextTab = crownMarket === "free" ? "free" : "paid";

  state.type = nextType;
  state.category = null;
  state.period = nextPeriod;
  state.tab = nextTab;
  const defaultsForType = getDefaultSortForType(nextType);
  state.sortColumn = defaultsForType.sortColumn;
  state.sortDirection = defaultsForType.sortDirection;

  updateViewClass();
  renderCategoryTree();
  updateTabButtonStyles();
  updateCategoryDropdownText(getDefaultCategoryDropdownLabel());
  updateCategorySelectedState(null);
  disableFreePaidTabs(false);
  updateControlDisabling();
  updateSearchFieldControls();
  updateColumnHeadersForMode();
  updatePeriodButtonStyles();
  updateCategoryDropdownVisibility();
  refresh();
  syncUrlWithState();

  const scroll = document.getElementById("clusterize-scroll");
  if (scroll) scroll.scrollTop = 0;

  trackEvent("crown_jump_clicked", {
    period: nextPeriod,
    tab: nextTab,
    type: nextType,
  });
}

/**
 * Handle sort column click
 */
function handleSortClick(column) {
  if (!acquireLock("sorting")) return;

  log(`[SORT] Column clicked: ${column}`);

  // Toggle direction if same column
  if (state.sortColumn === column) {
    state.sortDirection = state.sortDirection === "asc" ? "desc" : "asc";
  } else {
    state.sortColumn = column;
    // Numeric columns start desc, text asc.
    const numericColumns =
      state.type === "creator"
        ? ["templates", "templatesdelta", "components", "componentsdelta", "total", "totaldelta"]
        : ["change", "medals"];
    state.sortDirection = numericColumns.includes(column) ? "desc" : "asc";
  }

  sortFilteredRows();
  updateClusterize();
  updateSortIndicators();
  syncUrlWithState();

  releaseLock("sorting");

  trackEvent("sorting_changed", { column, direction: state.sortDirection });
}

/**
 * Handle search input
 */
let searchDebounceTimer = null;
function handleSearchInput(query) {
  // Debounce search input
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }

  // Update search-active class on input
  const searchInput = document.getElementById("rankings-search");
  if (searchInput) {
    searchInput.classList.toggle("search-active", query.length > 0);
  }

  searchDebounceTimer = setTimeout(() => {
    log(`[SEARCH] Query: "${query}"`);
    state.search = normalizeSearchValue(query);
    refresh();
    syncUrlWithState();

    if (query) {
      trackEvent("search_executed", { table: state.type });
    }
  }, 300);
}

// ============================================
// UI UPDATES
// ============================================

function updateTabButtonStyles() {
  const creatorsMode = state.type === "creator";
  const buttons = document.querySelectorAll("#ranking-tabs .tab-button");
  buttons.forEach((btn) => {
    const tab = btn.dataset.tab;
    const isActive = !creatorsMode && tab === state.tab;
    btn.classList.toggle("active", isActive);
  });

  // Update type buttons
  const templatesBtn = document.getElementById("templates-button");
  const componentsBtn = document.getElementById("components-button");
  const creatorsBtn = document.getElementById("creators-button");

  if (templatesBtn && componentsBtn && creatorsBtn) {
    templatesBtn.classList.toggle("active", state.type === "template");
    componentsBtn.classList.toggle("active", state.type === "component");
    creatorsBtn.classList.toggle("active", state.type === "creator");
  }
}

function updateControlDisabling() {
  const creatorsMode = state.type === "creator";
  const pricingTabs = document.querySelectorAll("#ranking-tabs .tab-button");
  pricingTabs.forEach((tab) => {
    tab.classList.toggle("tab-disabled", creatorsMode);
    tab.disabled = creatorsMode;
  });

  const periodButtons = document.querySelectorAll("#period-toggle .period-button");
  periodButtons.forEach((btn) => {
    btn.classList.toggle("tab-disabled", creatorsMode);
    btn.disabled = creatorsMode;
  });
}

function updateSearchFieldControls() {
  const creatorsMode = state.type === "creator";
  const container = document.getElementById("search-field-checkboxes");
  if (container) {
    container.classList.remove("show");
    container.classList.toggle("search-field-checkboxes-hidden", creatorsMode);
  }

  const checkboxIds = [
    "search-field-rank",
    "search-field-change",
    "search-field-price",
    "search-field-name",
    "search-field-author",
  ];
  checkboxIds.forEach((id) => {
    const checkbox = document.getElementById(id);
    if (checkbox) checkbox.disabled = creatorsMode;
  });
}

function bindSortHeaderListeners() {
  document.querySelectorAll("th.sortable").forEach((header) => {
    header.onclick = () => handleSortClick(header.dataset.column);
  });
}

function updateColumnHeadersForMode() {
  const thead = document.querySelector("#rankings-table thead");
  const tfoot = document.getElementById("rankings-tfoot");
  if (!thead || !tfoot) return;
  mountTableStructure({ thead, tfoot });
  bindSortHeaderListeners();
}

function updateSortIndicators() {
  const headers = document.querySelectorAll("th.sortable");
  headers.forEach((header) => {
    const column = header.dataset.column;
    const upArrow = header.querySelector(".sort-arrow-up");
    const downArrow = header.querySelector(".sort-arrow-down");

    if (upArrow) upArrow.classList.remove("active");
    if (downArrow) downArrow.classList.remove("active");

    if (column === state.sortColumn) {
      if (state.sortDirection === "asc" && upArrow) {
        upArrow.classList.add("active");
      } else if (downArrow) {
        downArrow.classList.add("active");
      }
    }
  });
}

function updateCategoryDropdownVisibility() {
  const container = document.querySelector(".rankings-container");
  if (container) {
    if (state.type === "component" || state.type === "creator") {
      container.classList.add("components-mode");
    } else {
      container.classList.remove("components-mode");
    }
  }

  // Disable category dropdown only for creators
  const dropdownWrapper = document.querySelector(".category-dropdown-wrapper");
  const dropdownButton = document.getElementById("category-dropdown-button");
  const shouldDisable = state.type === "creator";

  if (dropdownWrapper) {
    dropdownWrapper.classList.toggle("tab-disabled", shouldDisable);
    dropdownWrapper.removeAttribute("data-tooltip");
  }

  if (dropdownButton) {
    dropdownButton.disabled = shouldDisable;
  }
}

function updateCategoryDropdownText(text) {
  const textEl = document.getElementById("category-dropdown-text");
  const buttonEl = document.getElementById("category-dropdown-button");
  const arrowEl = document.querySelector("#category-dropdown-button .dropdown-arrow");
  const isSelected = !DEFAULT_CATEGORY_DROPDOWN_LABELS.has(text);

  if (textEl) {
    const displayText = DEFAULT_CATEGORY_DROPDOWN_LABELS.has(text) ? text : normalizeCategoryDisplayName(text);
    textEl.textContent = displayText;
  }

  if (buttonEl) {
    buttonEl.classList.toggle("active", isSelected);
  }

  if (arrowEl) {
    if (DEFAULT_CATEGORY_DROPDOWN_LABELS.has(text)) {
      arrowEl.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    } else {
      arrowEl.innerHTML = `<span class="category-clear-btn">✕</span>`;
      arrowEl.querySelector(".category-clear-btn")?.addEventListener("click", (e) => {
        e.stopPropagation();
        handleCategoryClear();
      });
    }
  }
}

function updateCategorySelectedState(categoryId) {
  // Remove selected class from all category items
  document.querySelectorAll(".category-item.selected").forEach((el) => {
    el.classList.remove("selected");
  });

  // Add selected class to the matching category item
  if (categoryId) {
    const item = document.querySelector(`.category-item[data-category-id="${categoryId}"]`);
    if (item) {
      item.classList.add("selected");
    }
  }
}

function disableFreePaidTabs(disabled) {
  const tabs = document.querySelectorAll(
    '#ranking-tabs .tab-button[data-tab="free"], #ranking-tabs .tab-button[data-tab="paid"]',
  );
  tabs.forEach((tab) => {
    tab.classList.toggle("tab-disabled", disabled);
    // Add/remove "Coming Soon" tooltip icon
    const existingIcon = tab.querySelector(".tooltip-icon");
    if (disabled && !existingIcon) {
      const icon = document.createElement("span");
      icon.className = "tooltip-icon";
      icon.setAttribute("data-tooltip", "Coming Soon");
      icon.textContent = "?";
      tab.appendChild(icon);
    } else if (!disabled && existingIcon) {
      existingIcon.remove();
    }
  });
}

function updatePeriodButtonStyles() {
  const lastWeekButton = document.getElementById("last-week-button");
  const allTimeButton = document.getElementById("all-time-button");
  const creatorsMode = state.type === "creator";

  // Update active states
  if (lastWeekButton) {
    lastWeekButton.classList.toggle("active", !creatorsMode && state.period === "lastWeek");
  }
  if (allTimeButton) {
    allTimeButton.classList.toggle("active", !creatorsMode && state.period === "allTime");
  }

  // Keep period toggle enabled for category views
  if (lastWeekButton) {
    lastWeekButton.classList.toggle("tab-disabled", creatorsMode);
    lastWeekButton.disabled = creatorsMode;
    const existingIcon = lastWeekButton.querySelector(".tooltip-icon");
    if (existingIcon) existingIcon.remove();
  }
  if (allTimeButton) {
    allTimeButton.classList.toggle("tab-disabled", creatorsMode);
    allTimeButton.disabled = creatorsMode;
  }
}

function updateTableTitle() {
  const titleMain = document.getElementById("table-title-main");
  const titleSubtitle = document.getElementById("table-title-subtitle");
  if (!titleMain) return;

  if (state.type === "creator") {
    const creatorCount = state.filteredRows?.length ?? 0;
    titleMain.innerHTML = `<span class="category-title-link">Creators</span>`;
    if (titleSubtitle) {
      titleSubtitle.innerHTML = `<span class="subtitle-count">${creatorCount.toLocaleString()} creators</span>`;
    }
    return;
  }

  const externalIcon = `<svg class="inline-block ml-1 -mt-0.5" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>`;

  // Build subtitle parts
  const periodLabel = state.period === "lastWeek" ? "Last Week" : "All Time";
  const pricingLabel = state.tab === "all" ? "Mixed Price" : state.tab === "free" ? "Free only" : "Paid only";
  const itemCount = state.filteredRows?.length ?? 0;
  const typeLabel = state.type === "template" ? "Templates" : "Components";
  const typePath = state.type === "template" ? "templates" : "components";

  // Build subtitle: Pricing · Period · Count
  // Dollar icon: detailed fill
  const pricingIcon = `<svg class="subtitle-icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M13 3.5C13 2.94772 12.5523 2.5 12 2.5C11.4477 2.5 11 2.94772 11 3.5V4.0592C9.82995 4.19942 8.75336 4.58509 7.89614 5.1772C6.79552 5.93745 6 7.09027 6 8.5C6 9.77399 6.49167 10.9571 7.5778 11.7926C8.43438 12.4515 9.58764 12.8385 11 12.959V17.9219C10.2161 17.7963 9.54046 17.5279 9.03281 17.1772C8.32378 16.6874 8 16.0903 8 15.5C8 14.9477 7.55228 14.5 7 14.5C6.44772 14.5 6 14.9477 6 15.5C6 16.9097 6.79552 18.0626 7.89614 18.8228C8.75336 19.4149 9.82995 19.8006 11 19.9408V20.5C11 21.0523 11.4477 21.5 12 21.5C12.5523 21.5 13 21.0523 13 20.5V19.9435C14.1622 19.8101 15.2376 19.4425 16.0974 18.8585C17.2122 18.1013 18 16.9436 18 15.5C18 14.1934 17.5144 13.0022 16.4158 12.1712C15.557 11.5216 14.4039 11.1534 13 11.039V6.07813C13.7839 6.20366 14.4596 6.47214 14.9672 6.82279C15.6762 7.31255 16 7.90973 16 8.5C16 9.05228 16.4477 9.5 17 9.5C17.5523 9.5 18 9.05228 18 8.5C18 7.09027 17.2045 5.93745 16.1039 5.17721C15.2467 4.58508 14.1701 4.19941 13 4.0592V3.5ZM11 6.07814C10.2161 6.20367 9.54046 6.47215 9.03281 6.8228C8.32378 7.31255 8 7.90973 8 8.5C8 9.22601 8.25834 9.79286 8.79722 10.2074C9.24297 10.5503 9.94692 10.8384 11 10.9502V6.07814ZM13 13.047V17.9263C13.7911 17.8064 14.4682 17.5474 14.9737 17.204C15.6685 16.7321 16 16.1398 16 15.5C16 14.7232 15.7356 14.1644 15.2093 13.7663C14.7658 13.4309 14.0616 13.1537 13 13.047Z"/></svg>`;
  // Stopwatch icon: minimal clock
  const periodIcon = `<svg class="subtitle-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10 3H14M12 9V13L14 15M20 13C20 17.4183 16.4183 21 12 21C7.58172 21 4 17.4183 4 13C4 8.58172 7.58172 5 12 5C16.4183 5 20 8.58172 20 13Z"/></svg>`;

  const periodClass = state.period === "lastWeek" ? "subtitle-period subtitle-lastweek" : "subtitle-period";
  const subtitleParts = [];
  subtitleParts.push(`<span class="subtitle-pricing">${pricingIcon} ${pricingLabel}</span>`);
  subtitleParts.push(`<span class="${periodClass}">${periodIcon} ${periodLabel}</span>`);
  subtitleParts.push(`<span class="subtitle-count">${itemCount.toLocaleString()} items</span>`);

  if (titleSubtitle) {
    titleSubtitle.innerHTML = subtitleParts.join(' <span class="subtitle-sep">·</span> ');
  }

  // Update main title with link
  if (state.category) {
    const activeMeta = getActiveCategoryMeta();
    const meta = activeMeta[state.category];
    const categoryDisplayName = meta
      ? normalizeCategoryDisplayName(meta.name)
      : normalizeCategoryDisplayName(state.category);
    const period = state.period === "lastWeek" ? "7" : "all";
    const pricing = state.tab === "free" ? "&pricing=free" : state.tab === "paid" ? "&pricing=paid" : "";
    const categoryUrl = meta?.url ? `${meta.url}?period=${period}${pricing}` : "#";
    const titlePrefix = state.tab === "free" ? "Free " : state.tab === "paid" ? "Paid " : "";
    titleMain.innerHTML = `<a href="${categoryUrl}" target="_blank" rel="noopener noreferrer" class="category-title-link hover:underline">${titlePrefix}'${categoryDisplayName}' ${typeLabel}${externalIcon}</a>`;
  } else {
    const pricingParam = state.tab === "free" ? "&pricing=free" : state.tab === "paid" ? "&pricing=paid" : "";
    const periodParam = state.period === "lastWeek" ? `?period=7${pricingParam}` : `?period=all${pricingParam}`;
    const marketplaceUrl = `https://www.framer.com/marketplace/${typePath}/${periodParam}`;
    const titlePrefix = state.tab === "free" ? "Free" : state.tab === "paid" ? "Paid" : "All";
    titleMain.innerHTML = `<a href="${marketplaceUrl}" target="_blank" rel="noopener noreferrer" class="category-title-link hover:underline">${titlePrefix} ${typeLabel}${externalIcon}</a>`;
  }
}

/**
 * Update table footer with dynamic stats based on filtered rows
 */
function updateTableFooter() {
  const creatorsFootCount = document.getElementById("tfoot-creators-count");
  const creatorsFootTemplates = document.getElementById("tfoot-creators-templates");
  const creatorsFootTemplatesDelta = document.getElementById("tfoot-creators-templatesdelta");
  const creatorsFootComponents = document.getElementById("tfoot-creators-components");
  const creatorsFootComponentsDelta = document.getElementById("tfoot-creators-componentsdelta");
  const creatorsFootTotal = document.getElementById("tfoot-creators-total");
  const creatorsFootTotalDelta = document.getElementById("tfoot-creators-totaldelta");
  const tfootRank = document.getElementById("tfoot-rank");
  const tfootChange = document.getElementById("tfoot-change");
  const tfootPrice = document.getElementById("tfoot-price");
  const tfootName = document.getElementById("tfoot-name");
  const tfootAuthor = document.getElementById("tfoot-author");

  if (!tfootRank && !creatorsFootCount) return;

  const filteredItems = state.filteredRows?.map((r) => r.item) || [];
  const count = filteredItems.length;
  const context = getCurrentContext();

  if (state.type === "creator") {
    let templatesTotal = 0;
    let componentsTotal = 0;
    let totalTotal = 0;
    let templatesDeltaPos = 0;
    let templatesDeltaNegAbs = 0;
    let componentsDeltaPos = 0;
    let componentsDeltaNegAbs = 0;
    let totalDeltaPos = 0;
    let totalDeltaNegAbs = 0;

    const addDelta = (rawValue, applyPos, applyNegAbs) => {
      const value = Number(rawValue);
      if (!Number.isFinite(value)) return;
      if (value > 0) applyPos(value);
      else if (value < 0) applyNegAbs(Math.abs(value));
    };

    for (const item of filteredItems) {
      const stats = item?.creatorStats || {};
      templatesTotal += Number(stats.totalTemplates || 0);
      componentsTotal += Number(stats.totalComponents || 0);
      totalTotal += Number(stats.totalItems || 0);
      addDelta(
        stats.totalTemplatesDelta1d,
        (value) => {
          templatesDeltaPos += value;
        },
        (value) => {
          templatesDeltaNegAbs += value;
        },
      );
      addDelta(
        stats.totalComponentsDelta1d,
        (value) => {
          componentsDeltaPos += value;
        },
        (value) => {
          componentsDeltaNegAbs += value;
        },
      );
      addDelta(
        stats.totalItemsDelta1d,
        (value) => {
          totalDeltaPos += value;
        },
        (value) => {
          totalDeltaNegAbs += value;
        },
      );
    }

    const formatDeltaTotalsHtml = (positive, negativeAbs) =>
      `<span class="tfoot-change-positive">+${positive.toLocaleString()}</span><span class="tfoot-change-sep">/</span><span class="tfoot-change-negative">-${negativeAbs.toLocaleString()}</span>`;

    if (creatorsFootCount) {
      const label = count === 1 ? "creator" : "creators";
      creatorsFootCount.textContent = `${count.toLocaleString()} ${label}`;
    }
    if (creatorsFootTemplates) creatorsFootTemplates.textContent = templatesTotal.toLocaleString();
    if (creatorsFootComponents) creatorsFootComponents.textContent = componentsTotal.toLocaleString();
    if (creatorsFootTotal) creatorsFootTotal.textContent = totalTotal.toLocaleString();
    if (creatorsFootTemplatesDelta) {
      creatorsFootTemplatesDelta.innerHTML = formatDeltaTotalsHtml(templatesDeltaPos, templatesDeltaNegAbs);
    }
    if (creatorsFootComponentsDelta) {
      creatorsFootComponentsDelta.innerHTML = formatDeltaTotalsHtml(componentsDeltaPos, componentsDeltaNegAbs);
    }
    if (creatorsFootTotalDelta) {
      creatorsFootTotalDelta.innerHTML = formatDeltaTotalsHtml(totalDeltaPos, totalDeltaNegAbs);
    }
    return;
  }

  // Calculate change event counters
  let newCount = 0;
  let movedInCount = 0;
  let comebackCount = 0;
  let priceChangedCount = 0;
  for (const item of filteredItems) {
    const change = item.ranks?.[context]?.[1];
    if (change === "new") newCount++;
    else if (change === "moved-in") movedInCount++;
    else if (change === "comeback") comebackCount++;
    else if (change === "price_change") priceChangedCount++;
  }

  // Calculate price stats
  const freeCount = filteredItems.filter((t) => t.price === "Free").length;
  const paidCount = filteredItems.filter((t) => t.price !== "Free").length;

  // Calculate mean/median for paid items
  const paidPrices = [];
  for (const item of filteredItems) {
    if (item.price !== "Free") {
      const priceNum = parseFloat(item.price?.replace(/[$,]/g, ""));
      if (!Number.isNaN(priceNum) && priceNum > 0) {
        paidPrices.push(priceNum);
      }
    }
  }
  let meanPrice = 0;
  let medianPrice = 0;
  if (paidPrices.length > 0) {
    meanPrice = paidPrices.reduce((a, b) => a + b, 0) / paidPrices.length;
    paidPrices.sort((a, b) => a - b);
    const mid = Math.floor(paidPrices.length / 2);
    medianPrice = paidPrices.length % 2 !== 0 ? paidPrices[mid] : (paidPrices[mid - 1] + paidPrices[mid]) / 2;
  }

  // Calculate unique authors
  const uniqueAuthors = new Set(filteredItems.map((t) => t.authorName).filter(Boolean));

  // Update tfoot cells
  // Rank column: count + free/paid with custom tooltip
  const rankTooltipTop = `${count.toLocaleString()} items`;
  const rankTooltipBottom = `${freeCount.toLocaleString()} free / ${paidCount.toLocaleString()} paid`;
  tfootRank.innerHTML = `
    <div class="with-tooltip">
      <div class="tfoot-rank-total">${count.toLocaleString()}</div>
      <div class="tfoot-rank-breakdown">${freeCount.toLocaleString()} / ${paidCount.toLocaleString()}</div>
      <div class="tooltip top">
        <div class="tooltip-item">${rankTooltipTop}</div>
        <div class="tooltip-item">${rankTooltipBottom}</div>
      </div>
    </div>
  `;

  // Change column: new / moved-in / comeback / repriced counters
  tfootChange.innerHTML = `<div class="with-tooltip"><div class="tfoot-change-events"><span class="tfoot-change-new">${newCount}</span> <span class="tfoot-change-sep">/</span> <span class="tfoot-change-movedin">${movedInCount}</span> <span class="tfoot-change-sep">/</span> <span class="tfoot-change-comeback">${comebackCount}</span> <span class="tfoot-change-sep">/</span> <span class="tfoot-change-price">${priceChangedCount}</span></div><div class="tooltip top"><div class="tooltip-item">Label counters</div><div class="tooltip-item">new / moved-in / comeback / repriced</div></div></div>`;

  // Price column: avg/mid
  const avgStr = meanPrice > 0 ? `$${Math.round(meanPrice)}` : "-";
  const midStr = medianPrice > 0 ? `$${Math.round(medianPrice)}` : "-";
  tfootPrice.innerHTML = `<div class="with-tooltip"><div class="tfoot-price-label">avg / mid</div><div class="tfoot-price-values">${avgStr} / ${midStr}</div><div class="tooltip top"><div class="tooltip-item">Average price vs median price</div></div></div>`;

  // Name column: empty
  tfootName.textContent = "";

  // Creator column: unique creator count
  const creatorLabel = uniqueAuthors.size === 1 ? "creator" : "creators";
  tfootAuthor.textContent = `${uniqueAuthors.size.toLocaleString()} ${creatorLabel}`;
}

function closeCategoryDropdown() {
  const panel = document.getElementById("category-dropdown-panel");
  if (panel) panel.classList.remove("open");
}

// ============================================
// CATEGORY TREE
// ============================================

/**
 * Sort category tree alphabetically at each level independently
 * Preserves all category properties (id, url, pageUrl, etc.)
 */
function sortCategoryTree(categories) {
  // Sort this level by name (case-insensitive)
  const sorted = [...categories].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

  // Recursively sort children at each level
  return sorted.map((cat) => ({
    ...cat,
    children: cat.children && cat.children.length > 0 ? sortCategoryTree(cat.children) : cat.children,
  }));
}

function renderCategoryTree() {
  const container = document.getElementById("category-tree");
  if (!container) return;

  container.innerHTML = "";

  // "All Categories" option
  const allOption = document.createElement("div");
  allOption.className = "category-node category-all-option";
  allOption.innerHTML = `
    <div class="category-item category-item-all">
      <span class="category-label-text category-label-clickable">${getAllCategoriesOptionLabel()}</span>
    </div>
  `;
  allOption.querySelector(".category-label-text").addEventListener("click", (e) => {
    e.stopPropagation();
    handleCategoryClear();
    closeCategoryDropdown();
  });
  container.appendChild(allOption);

  // Render tree - sort alphabetically at each level
  const activeTree = getActiveCategoryTree();
  if (activeTree.length > 0) {
    const sortedTree = sortCategoryTree(activeTree);
    container.appendChild(renderCategoryLevel(sortedTree, 1));
  }
}

function renderCategoryLevel(categories, level) {
  const fragment = document.createDocumentFragment();
  const isTopLevel = level === 1;

  for (const cat of categories) {
    const node = document.createElement("div");
    node.className = `category-node category-level-${level}`;

    const hasChildren = cat.children && cat.children.length > 0;
    const count = countCategoryItems(cat.id);

    const item = document.createElement("div");
    item.className = "category-item";
    item.dataset.categoryId = cat.id;

    // Expand icon
    const expandIcon = document.createElement("span");
    expandIcon.className = `category-expand-icon ${hasChildren ? "" : "hidden"}`;
    expandIcon.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M9 6L15 12L9 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    item.appendChild(expandIcon);

    const displayName = isTopLevel ? normalizeCategoryDisplayName(cat.name) : normalizeCategoryDisplayName(cat.name);

    const label = document.createElement("span");
    label.className = "category-label-text";
    label.innerHTML = `${displayName} <span class="category-item-count">(${count})</span>`;
    item.appendChild(label);

    // Click handlers
    expandIcon.addEventListener("click", (e) => {
      e.stopPropagation();
      const children = node.querySelector(".category-children");
      if (children) {
        children.classList.toggle("expanded");
        expandIcon.classList.toggle("expanded");
      }
    });

    // Whole row triggers category selection (except expand icon which stops propagation)
    item.addEventListener("click", (_e) => {
      handleCategorySelect(cat.id, cat.name);
    });

    node.appendChild(item);

    // Children
    if (hasChildren) {
      const childrenContainer = document.createElement("div");
      childrenContainer.className = "category-children";
      childrenContainer.appendChild(renderCategoryLevel(cat.children, level + 1));
      node.appendChild(childrenContainer);
    }

    fragment.appendChild(node);
  }

  return fragment;
}

function countCategoryItems(categoryId) {
  return state.allItems.filter((item) => item.type === state.type && item.categories?.includes(categoryId)).length;
}

// ============================================
// ANALYTICS
// ============================================

function trackEvent(eventName, metadata = {}) {
  if (typeof window.sa_event === "function") {
    window.sa_event(eventName, { ...metadata, type: state.type });
  }
}

// ============================================
// INITIALIZATION
// ============================================

async function loadData() {
  log("[INIT] Loading data...");
  const startTime = performance.now();

  state.api.enabled = isApiModeEnabled();

  if (state.api.enabled) {
    await loadApiContextForCurrentState(true);
    const duration = Math.round(performance.now() - startTime);
    log(`[INIT] Loaded API context '${state.api.activeContextKey || "-"}' in ${duration}ms`);
    return { currentDateStr: state.api.lastUpdated || "-" };
  }

  const response = await fetch("/ranks-data.json");
  if (!response.ok) {
    throw new Error(`Failed to load data: ${response.status}`);
  }

  const data = await response.json();

  const creators = Array.isArray(data.creators) ? data.creators : [];
  const creatorItems = creators.map((creator, index) => {
    const slug = String(creator.creatorSlug || "").trim();
    const creatorUrl =
      typeof creator.creatorUrl === "string" && creator.creatorUrl.length > 0
        ? creator.creatorUrl
        : slug
          ? `https://framer.com/@${slug}`
          : null;
    return {
      id: `creator-${slug || index}`,
      type: "creator",
      name: creator.creatorName || slug || "Unknown creator",
      creatorSlug: slug,
      creatorUrl,
      url: creatorUrl || "https://framer.com/marketplace/",
      thumbnail: creator.creatorAvatar || null,
      price: "-",
      authorName: slug ? `@${slug}` : "-",
      authorUrl: creatorUrl,
      ranks: {},
      categories: [],
      badges: { crowns: [], medals: [], medalScore: 0 },
      creatorStats: {
        totalItems: Number(creator.totalItems || 0),
        totalTemplates: Number(creator.totalTemplates || 0),
        totalComponents: Number(creator.totalComponents || 0),
        totalItemsDelta1d:
          creator.totalItemsDelta1d === null || creator.totalItemsDelta1d === undefined
            ? null
            : Number(creator.totalItemsDelta1d),
        totalTemplatesDelta1d:
          creator.totalTemplatesDelta1d === null || creator.totalTemplatesDelta1d === undefined
            ? null
            : Number(creator.totalTemplatesDelta1d),
        totalComponentsDelta1d:
          creator.totalComponentsDelta1d === null || creator.totalComponentsDelta1d === undefined
            ? null
            : Number(creator.totalComponentsDelta1d),
      },
    };
  });

  state.allItems = [...(data.items || []), ...creatorItems];
  state.templateCategoryMeta = data.templateCategoryMeta || data.categoryMeta || {};
  state.templateCategoryTree = data.templateCategoryTree || data.categoryTree || [];
  state.componentCategoryMeta = data.componentCategoryMeta || {};
  state.componentCategoryTree = data.componentCategoryTree || [];
  // Backward-compatible aliases
  state.categoryMeta = state.templateCategoryMeta;
  state.categoryTree = state.templateCategoryTree;

  if (typeof window !== "undefined") {
    window.__RANKS_TEMPLATE_CATEGORY_META__ = state.templateCategoryMeta;
    window.__RANKS_COMPONENT_CATEGORY_META__ = state.componentCategoryMeta;
    window.__RANKS_CATEGORY_META__ = {
      ...state.templateCategoryMeta,
      ...state.componentCategoryMeta,
    };
    const uiFlags = data.uiFlags || {};
    window.__RANKS_SPARKLINE_LABEL_MODE__ = uiFlags.sparklineLabelMode === "all" ? "all" : "none";
    window.__RANKS_SPARKLINE_TITLE_ATTR__ = uiFlags.sparklineTitleAttr === true;
  }

  // Update date display
  const dateEl = document.getElementById("current-date");
  if (dateEl) {
    dateEl.textContent = data.currentDateStr;
  }

  const duration = Math.round(performance.now() - startTime);
  log(`[INIT] Loaded ${state.allItems.length} items in ${duration}ms`);

  return data;
}

function renderRows() {
  if (!acquireLock("rendering")) return;

  log("[INIT] Rendering all rows...");
  const startTime = performance.now();

  state.allRows = renderAllRows(state.allItems);

  const duration = Math.round(performance.now() - startTime);
  log(`[INIT] Rendered ${state.allRows.length} rows in ${duration}ms`);

  releaseLock("rendering");
}

function initClusterize() {
  log("[INIT] Initializing Clusterize...");

  // Initial filter and sort
  state.filteredRows = filterRows();
  sortFilteredRows();

  let htmlRows = state.sortedIndices.map((i) => state.filteredRows[i].html);
  if (htmlRows.length === 0) {
    const emptyMessage = state.type === "creator" ? "No creators found" : "No results found";
    htmlRows = [buildNoResultsRow(emptyMessage)];
  }
  htmlRows.push(buildFooterStretchRow());

  // biome-ignore lint/correctness/noUndeclaredVariables: Clusterize is external
  state.clusterize = new Clusterize({
    scrollId: "clusterize-scroll",
    contentId: "clusterize-content",
    rows: htmlRows,
    tag: "tr",
    // Keep larger blocks to avoid scroll jitter/jumps with heavy table rows.
    rows_in_block: 15,
    blocks_in_cluster: 2,
    show_no_data_row: false,
  });

  // Initialize sparklines for initial rows
  initSparklines();
  syncFooterStretchRowHeight();

  log(`[INIT] Clusterize initialized with ${htmlRows.length} rows`);
}

function setupEventListeners() {
  // Type buttons
  document.getElementById("templates-button")?.addEventListener("click", () => handleTypeSwitch("template"));
  document.getElementById("components-button")?.addEventListener("click", () => handleTypeSwitch("component"));
  document.getElementById("creators-button")?.addEventListener("click", () => handleTypeSwitch("creator"));

  // Tab buttons
  document.querySelectorAll("#ranking-tabs .tab-button").forEach((btn) => {
    btn.addEventListener("click", () => handleTabSwitch(btn.dataset.tab));
  });

  // Period buttons
  document.getElementById("all-time-button")?.addEventListener("click", () => handlePeriodSwitch("allTime"));
  document.getElementById("last-week-button")?.addEventListener("click", () => handlePeriodSwitch("lastWeek"));

  // Sort headers
  bindSortHeaderListeners();

  // Search input
  const searchInput = document.getElementById("rankings-search");
  if (searchInput) {
    log("[INIT] Search input found, attaching listener");
    searchInput.addEventListener("input", (e) => {
      handleSearchInput(e.target.value);
      // Show checkboxes when search has value
      const checkboxes = document.getElementById("search-field-checkboxes");
      if (checkboxes) {
        if (state.type === "creator") {
          checkboxes.classList.remove("show");
        } else if (searchInput.value) checkboxes.classList.add("show");
        else if (document.activeElement !== searchInput) checkboxes.classList.remove("show");
      }
    });

    // Show/hide search field checkboxes on focus/blur
    searchInput.addEventListener("focus", () => {
      const checkboxes = document.getElementById("search-field-checkboxes");
      if (checkboxes && state.type !== "creator") checkboxes.classList.add("show");
    });
    searchInput.addEventListener("blur", () => {
      // Delay to allow checkbox clicks
      setTimeout(() => {
        const checkboxes = document.getElementById("search-field-checkboxes");
        if (checkboxes && !searchInput.value) checkboxes.classList.remove("show");
      }, 200);
    });
  } else {
    console.warn("[INIT] Search input NOT found!");
  }

  // Search field checkboxes
  const fieldCheckboxes = {
    "search-field-rank": "searchRank",
    "search-field-change": "searchChange",
    "search-field-price": "searchPrice",
    "search-field-name": "searchName",
    "search-field-author": "searchAuthor",
  };
  for (const [id, field] of Object.entries(fieldCheckboxes)) {
    const checkbox = document.getElementById(id);
    if (checkbox) {
      checkbox.addEventListener("change", (e) => {
        state.searchFields[field] = e.target.checked;
        log(`[SEARCH] Field ${field} = ${e.target.checked}`);
        if (state.search) {
          refresh();
        }
      });
    }
  }

  // Category dropdown
  document.getElementById("category-dropdown-button")?.addEventListener("click", (e) => {
    e.stopPropagation();
    const panel = document.getElementById("category-dropdown-panel");
    if (panel) {
      panel.classList.toggle("open");
    }
  });

  // Close dropdown on outside click
  document.addEventListener("click", (e) => {
    const panel = document.getElementById("category-dropdown-panel");
    const button = document.getElementById("category-dropdown-button");
    if (panel && !panel.contains(e.target) && button && !button.contains(e.target)) {
      panel.classList.remove("open");
    }
  });

  const isMobileTooltipMode = () => window.matchMedia("(max-width: 900px)").matches;
  const closeMobileMedalTooltips = (exceptEl = null) => {
    if (!isMobileTooltipMode()) return;
    document.querySelectorAll(".medals-cell .with-tooltip.tooltip-open").forEach((el) => {
      if (exceptEl && el === exceptEl) return;
      el.classList.remove("tooltip-open");
    });
  };

  document.addEventListener(
    "click",
    (e) => {
      if (!isMobileTooltipMode()) return;
      const targetEl = e.target instanceof Element ? e.target : null;
      if (!targetEl) return;

      const jumpBtn = targetEl.closest(".medal-jump-btn");
      if (jumpBtn) return;

      const maybeTrigger = targetEl.closest(".with-tooltip");
      const trigger = maybeTrigger?.closest(".medals-cell") ? maybeTrigger : null;
      if (trigger) {
        const willOpen = !trigger.classList.contains("tooltip-open");
        closeMobileMedalTooltips();
        if (willOpen) trigger.classList.add("tooltip-open");
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      closeMobileMedalTooltips();
    },
    true,
  );

  document.getElementById("clusterize-scroll")?.addEventListener(
    "scroll",
    () => {
      closeMobileMedalTooltips();
    },
    { passive: true },
  );

  // Medal tooltip jump buttons
  document.addEventListener("click", (e) => {
    const btn = e.target.closest(".medal-jump-btn");
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const categoryId = btn.getAttribute("data-category-id");
    const rank = Number(btn.getAttribute("data-rank") || "0");
    const market = btn.getAttribute("data-market") || "all";
    const itemType = btn.getAttribute("data-item-type");
    handleMedalJump(categoryId, rank, market, itemType);
    closeMobileMedalTooltips();
  });

  // Crown tooltip jump buttons
  document.addEventListener("click", (e) => {
    const btn = e.target.closest(".crown-jump-btn");
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const crownPeriod = btn.getAttribute("data-crown-period");
    const crownMarket = btn.getAttribute("data-crown-market");
    const itemType = btn.getAttribute("data-item-type");
    handleCrownJump(crownPeriod, crownMarket, itemType);
  });

  // On mobile, move category dropdown into the search row slot.
  const categoryWrapper = document.querySelector(".category-dropdown-wrapper");
  const mobileCategorySlot = document.getElementById("mobile-category-slot");
  const originalCategoryParent = categoryWrapper?.parentElement || null;
  const originalCategoryNextSibling = categoryWrapper?.nextElementSibling || null;
  const syncCategoryPlacement = () => {
    if (!categoryWrapper || !mobileCategorySlot || !originalCategoryParent) return;
    const isMobile = window.matchMedia("(max-width: 650px)").matches;
    if (isMobile) {
      if (categoryWrapper.parentElement !== mobileCategorySlot) {
        mobileCategorySlot.appendChild(categoryWrapper);
      }
      document.body.classList.add("category-placement-ready");
      return;
    }
    if (categoryWrapper.parentElement !== originalCategoryParent) {
      if (originalCategoryNextSibling && originalCategoryNextSibling.parentElement === originalCategoryParent) {
        originalCategoryParent.insertBefore(categoryWrapper, originalCategoryNextSibling);
      } else {
        originalCategoryParent.appendChild(categoryWrapper);
      }
    }
    document.body.classList.add("category-placement-ready");
  };
  syncCategoryPlacement();
  window.addEventListener("resize", syncCategoryPlacement);
  window.addEventListener("resize", syncFooterStretchRowHeight);

  window.addEventListener("popstate", () => {
    isApplyingUrlState = true;
    try {
      const resolved = resolveUrlStateFromLocation(
        {
          pathname: window.location.pathname,
          searchParams: new URLSearchParams(window.location.search),
        },
        {
          templateCategoryMeta: state.templateCategoryMeta,
          componentCategoryMeta: state.componentCategoryMeta,
          categoryMeta: state.categoryMeta,
        },
      );
      state.type = resolved.type;
      state.tab = resolved.tab;
      state.period = resolved.period;
      state.category = resolved.category;
      state.search = resolved.search;
      state.sortColumn = resolved.sortColumn;
      state.sortDirection = resolved.sortDirection;
      applyCurrentStateToUi({ closeDropdown: true, scrollTop: true });
      syncUrlWithState();
    } finally {
      isApplyingUrlState = false;
    }
  });

  // Pictures are always-on; toggle UI is hidden.
}

// ============================================
// FEEDBACK
// ============================================

/**
 * Initialize feedback modal and direct-to-Supabase submit logic.
 * No-op when feedback elements are not present.
 */
function initFeedback() {
  const triggers = Array.from(document.querySelectorAll(".js-feedback-trigger"));
  const overlay = document.getElementById("feedback-modal-overlay");
  const closeBtn = document.getElementById("feedback-close");
  const form = document.getElementById("feedback-form");
  const toast = document.getElementById("feedback-toast");

  if (triggers.length === 0 || !overlay || !closeBtn || !form || !toast) return;
  form.setAttribute("data-feedback-ready", "1");

  const fallbackConfig = {
    endpoint: "https://ecvnulsoqealbpmuyoif.functions.supabase.co/feedback-submit",
    publishableKey:
      "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVjdm51bHNvcWVhbGJwbXV5b2lmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzA5NjgxMDMsImV4cCI6MjA4NjU0NDEwM30.XdrqxKZNIHwg4FzSR8Fp5mTijMAlVJf0uFQSrkk1iuI",
  };

  const getFeedbackConfig = () => {
    // Optional runtime override from ranks.html without changing bundled JS.
    const runtimeConfig = window.FR_RANKS_FEEDBACK_CONFIG || {};
    const endpoint = (runtimeConfig.endpoint || fallbackConfig.endpoint || "").trim();
    const publishableKey = (runtimeConfig.publishableKey || fallbackConfig.publishableKey || "").trim();
    if (!endpoint || !publishableKey) return null;
    return { endpoint, publishableKey };
  };

  const closeModal = () => {
    overlay.classList.remove("open");
    document.body.style.overflow = "";
    form.reset();
  };

  for (const trigger of triggers) {
    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      overlay.classList.add("open");
      document.body.style.overflow = "hidden";
    });
  }

  closeBtn.addEventListener("click", closeModal);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) closeModal();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && overlay.classList.contains("open")) closeModal();
  });

  form.addEventListener("submit", async (e) => {
    e.preventDefault();

    const hp = document.getElementById("feedback-hp");
    if (hp?.value) return;

    const submitBtn = document.getElementById("feedback-submit");
    if (!submitBtn) return;
    const originalText = submitBtn.textContent;

    try {
      submitBtn.disabled = true;
      submitBtn.textContent = "Sending...";

      const message = (document.getElementById("feedback-message")?.value || "").trim();
      const email = (document.getElementById("feedback-email")?.value || "").trim();
      if (!message) throw new Error("Message is required");
      if (message.length > 2000) throw new Error("Message exceeds max length");

      const payload = {
        message,
        email,
        page: "ranks",
        type: "suggestion",
      };

      const config = getFeedbackConfig();
      if (!config) throw new Error("Feedback endpoint config is missing");

      const response = await fetch(config.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: config.publishableKey,
          Authorization: `Bearer ${config.publishableKey}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const responseText = await response.text();
        throw new Error(`Feedback submit failed (${response.status}): ${responseText}`);
      }

      closeModal();
      toast.classList.add("show");
      setTimeout(() => toast.classList.remove("show"), 4000);
    } catch (error) {
      console.error("[FEEDBACK] Error:", error);
      alert("Failed to send feedback. Please try again later.");
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = originalText;
    }
  });
}

export async function init() {
  log("[INIT] Starting initialization...");
  const startTime = performance.now();
  setBootingState(true);
  initThemeController();

  try {
    perfState.enabled = detectPerfEnabled();
    await loadData();
    isApplyingUrlState = true;
    const resolved = resolveUrlStateFromLocation(
      {
        pathname: window.location.pathname,
        searchParams: new URLSearchParams(window.location.search),
      },
      {
        templateCategoryMeta: state.templateCategoryMeta,
        componentCategoryMeta: state.componentCategoryMeta,
        categoryMeta: state.categoryMeta,
      },
    );
    state.type = resolved.type;
    state.tab = resolved.tab;
    state.period = resolved.period;
    state.category = resolved.category;
    state.search = resolved.search;
    state.sortColumn = resolved.sortColumn;
    state.sortDirection = resolved.sortDirection;
    if (state.api.enabled) {
      await loadApiContextForCurrentState(true);
    }
    isApplyingUrlState = false;
    injectCategoryCssRules(); // Inject CSS for category views
    renderRows();
    clearFilterCache();
    initClusterize();
    setupEventListeners();
    initFeedback();
    renderCategoryTree();
    updateViewClass();
    updateTabButtonStyles();
    updateControlDisabling();
    updateSearchFieldControls();
    updateColumnHeadersForMode();
    updatePeriodButtonStyles();
    updateTableTitle();
    updateTableFooter();
    updateCategoryDropdownVisibility();
    const activeMeta = getActiveCategoryMeta();
    const categoryName =
      state.category && activeMeta[state.category]
        ? activeMeta[state.category].name
        : getDefaultCategoryDropdownLabel();
    updateCategoryDropdownText(categoryName);
    updateCategorySelectedState(state.category);
    const searchInput = document.getElementById("rankings-search");
    if (searchInput) {
      searchInput.value = state.search || "";
      searchInput.classList.toggle("search-active", Boolean(state.search));
    }
    syncUrlWithState();
    setBootingState(false);

    const duration = Math.round(performance.now() - startTime);
    log(`[INIT] Initialization complete in ${duration}ms`);
  } catch (error) {
    console.error("[INIT] Error:", error);
    setBootingState(true);
    const dateEl = document.getElementById("current-date");
    if (dateEl) dateEl.textContent = "Error loading data";
    const titleMain = document.getElementById("table-title-main");
    const titleSubtitle = document.getElementById("table-title-subtitle");
    if (titleMain) titleMain.textContent = "Failed to load rankings data";
    if (titleSubtitle) titleSubtitle.textContent = "Check JSON generation and refresh.";
  }
}

// Export for compatibility
export {
  state,
  handleTypeSwitch,
  handleTabSwitch,
  handlePeriodSwitch,
  handleMedalJump,
  disableFreePaidTabs,
  updateTableTitle,
  buildNoResultsRow,
  buildFooterStretchRow,
};

// Expose debug info on window for console access
if (typeof window !== "undefined") {
  async function runTabSwitchBenchmark(options = {}) {
    const iterations = Math.max(3, Number(options.iterations ?? 30));
    const sequence = ["all", "free", "paid"];
    const previousPerfFlag = perfState.enabled;
    perfState.enabled = true;
    perfState.history = [];

    // Ensure stable baseline.
    state.type = "template";
    state.category = null;
    state.period = "lastWeek";
    state.tab = "all";
    updateViewClass();
    refresh();

    const samples = [];
    for (let i = 0; i < iterations; i++) {
      const nextTab = sequence[i % sequence.length];
      if (state.tab === nextTab) {
        const fallbackTab = sequence[(i + 1) % sequence.length];
        handleTabSwitch(fallbackTab);
      }
      const start = performance.now();
      handleTabSwitch(nextTab);
      const elapsed = performance.now() - start;
      samples.push(elapsed);
    }

    const sorted = [...samples].sort((a, b) => a - b);
    const avg = samples.reduce((sum, ms) => sum + ms, 0) / samples.length;
    const p95 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))];
    const summary = {
      iterations: samples.length,
      avgMs: Math.round(avg * 100) / 100,
      p95Ms: Math.round(p95 * 100) / 100,
      minMs: Math.round(sorted[0] * 100) / 100,
      maxMs: Math.round(sorted[sorted.length - 1] * 100) / 100,
    };
    console.table(summary);
    console.table(perfState.history.slice(-Math.min(20, perfState.history.length)));
    perfState.enabled = previousPerfFlag;
    return { summary, samples, history: [...perfState.history] };
  }

  window.RanksDebug = {
    get state() {
      return state;
    },
    get filteredRows() {
      return state.filteredRows;
    },
    get sortedIndices() {
      return state.sortedIndices;
    },
    get allItems() {
      return state.allItems;
    },
    get allRows() {
      return state.allRows;
    },
    refresh,
    clearSortCache,
    clearFilterCache,
  };
  window.RanksPerf = {
    enable() {
      perfState.enabled = true;
      window.__RANKS_PERF__ = true;
    },
    disable() {
      perfState.enabled = false;
      window.__RANKS_PERF__ = false;
    },
    clear() {
      perfState.history = [];
    },
    get history() {
      return perfState.history;
    },
    get last() {
      return perfState.history[perfState.history.length - 1] || null;
    },
    runTabSwitchBenchmark,
  };
}
